import React, { useEffect, useState } from "react";
import { CalendarCheck2, X } from "lucide-react";
import { useSafeBackdropClose } from "../lib/modal";
import DateInput from "./DateInput";

/**
 * HandoverDateDialog — required-input dialog shown when a project is
 * moved into the Final Handover lifecycle stage. Captures the handover
 * date (defaults to today) and an optional note, then hands both back
 * to the caller which passes them to /projects/{id}/lifecycle.
 *
 * The date is REQUIRED — the confirm button is disabled until the
 * operator has entered one (backend also refuses the transition
 * without it as a safety net).
 */
export default function HandoverDateDialog({ project, onConfirm, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(project?.handover_date || today);
  const [note, setNote] = useState("");
  const canConfirm = !!date;
  const backdrop = useSafeBackdropClose(onCancel);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canConfirm) {
        onConfirm({ handover_date: date, note });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [date, note, canConfirm, onConfirm, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      {...backdrop}
      data-testid="handover-date-dialog"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarCheck2 size={16} style={{ color: "#0F8B84" }} />
            <h3 className="font-display text-lg font-bold">Move to Final Handover</h3>
          </div>
          <button
            type="button"
            className="btn-ghost !p-1"
            onClick={onCancel}
            aria-label="Close"
            data-testid="handover-date-close"
          >
            <X size={14} />
          </button>
        </div>
        <p className="muted text-xs mb-3">
          <strong>{project?.name || "This project"}</strong> is being completed.
          Capture the handover date so planned-vs-actual and yearly rollups stay accurate.
        </p>
        <label className="block mb-3">
          <div className="label !text-[10px] mb-1">Handover date <span className="text-red-600">*</span></div>
          <DateInput
            className="input-box w-full"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            autoFocus
            data-testid="handover-date-input"
          />
        </label>
        <label className="block mb-4">
          <div className="label !text-[10px] mb-1">Note (optional)</div>
          <textarea
            className="input-swiss w-full"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Snag list cleared, PPA active, monitoring live…"
            data-testid="handover-date-note"
          />
          <div className="text-[10px] muted mt-1">Tip: Ctrl/Cmd+Enter to confirm.</div>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={onCancel}
            data-testid="handover-date-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={!canConfirm}
            onClick={() => onConfirm({ handover_date: date, note })}
            data-testid="handover-date-confirm"
          >
            Complete project
          </button>
        </div>
      </div>
    </div>
  );
}
